import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { usePDFCurrentSearchResultElementContext } from '../../../contexts/PDFCurrentSearchResultElementContext';
import { usePDFViewerSearchContext } from '../../../contexts/PDFViewerSearch/PDFViewerSearchContext';
import { PDFTextHighlighter } from '../utils/PDFTextHighlighter';

type UseHighlightSearchResultsParams = {
  containerRef: RefObject<HTMLDivElement>;
  pageNumber: number;
};

export const useHighlightSearchResults = ({ containerRef, pageNumber }: UseHighlightSearchResultsParams) => {
  const { pageMatchesLength, pageMatches, currentResultIndex, getMatchesMapArray } = usePDFViewerSearchContext();
  const { setCurrentSearchResultElement, clearElementForPage } = usePDFCurrentSearchResultElementContext();

  const textHighlighterRef = useRef<PDFTextHighlighter>();

  useLayoutEffect(() => {
    textHighlighterRef.current = new PDFTextHighlighter(containerRef.current);
    return () => {
      textHighlighterRef.current?.cleanup();
    };
  }, [containerRef]);

  const highlightMatchedPhrases = useCallback(() => {
    textHighlighterRef.current?.highlightMatchedPhrases({
      pageMatches: pageMatches[pageNumber - 1],
      pageMatchesLength: pageMatchesLength[pageNumber - 1],
    });
  }, [pageMatches, pageMatchesLength, pageNumber]);

  const updateSelectedMatch = useCallback(() => {
    const matchesMapArray = getMatchesMapArray();
    const selectedMatch = matchesMapArray[currentResultIndex];
    if (!selectedMatch || selectedMatch.pageIndex !== pageNumber - 1) {
      textHighlighterRef.current?.clearSelectedMatch();
      clearElementForPage(pageNumber);
    } else {
      const selectedElements = textHighlighterRef.current?.changeSelectedMatch(selectedMatch.divIndex);
      if (selectedElements?.length) {
        setCurrentSearchResultElement({ pageNumber, element: selectedElements[0] as HTMLElement });
      } else {
        clearElementForPage(pageNumber);
      }
    }
  }, [clearElementForPage, currentResultIndex, getMatchesMapArray, pageNumber, setCurrentSearchResultElement]);

  const onTextLayerRendered = useCallback(
    (divs: HTMLElement[], texts: string[]) => {
      textHighlighterRef.current?.setTextMapping(divs, texts);

      highlightMatchedPhrases();
      updateSelectedMatch();
    },
    [highlightMatchedPhrases, updateSelectedMatch],
  );

  useEffect(() => {
    return () => {
      clearElementForPage(pageNumber);
    };
  }, [pageNumber, clearElementForPage]);

  useLayoutEffect(() => {
    highlightMatchedPhrases();
  }, [highlightMatchedPhrases]);

  useLayoutEffect(() => {
    updateSelectedMatch();
  }, [updateSelectedMatch, pageMatches, pageMatchesLength]);

  return {
    onTextLayerRendered,
  };
};
